import {
  DocumentData,
  DocumentReference,
  FieldValue,
} from "@atgof-firebase/firebase";
import { useProject } from "./useProject";
import { sheetsCollection } from "../common/sheet";
import _ from "lodash";
import { useDocuments } from "./useDocuments";

type SlateNumber = { prefix: string; n: number; scenePath?: string };

export const cardNumberAttrs = [
  "pa:cameraCardNumber",
  "pa:soundCardNumber",
  "pa:cameraCardNumbers",
];

function parseSlateNumber(
  slate: string | undefined,
  scenePath: string | undefined,
): SlateNumber | undefined {
  const m = slate?.match(/(\D*)(\d+)/);
  if (!m) return;
  return {
    prefix: m[1],
    n: parseInt(m[2]),
    ...(scenePath ? { scenePath } : {}),
  };
}

function formatSlateNumber({ prefix, n }: SlateNumber) {
  return `${prefix}${n}`;
}

export async function removeSlate(
  teamRef: DocumentReference | undefined,
  sheetIdToIgnore: string | undefined,
  slate: string | undefined,
  sceneRef: DocumentReference | undefined,
) {
  if (!teamRef) return;
  const candidate = parseSlateNumber(slate, undefined);
  if (!candidate) return;
  const latest = (await teamRef.get()).get("latestSlate") as
    | SlateNumber
    | undefined;
  if (candidate.prefix === latest?.prefix && candidate.n == latest?.n) {
    const project = teamRef?.parent?.parent;
    let q = project
      ? sheetsCollection(project)
          .where("category", "==", "pa")
          .where("team", "==", teamRef)
          .where("deleted", "==", false)
      : undefined;
    if (q && sceneRef) q = q.where("scene", "==", sceneRef);
    const res = q
      ? await q.orderBy("createdAt", "desc").limit(2).get()
      : undefined;
    const sheet = res?.docs.find((sheet) => sheet.id !== sheetIdToIgnore);
    const nextCandidate = parseSlateNumber(
      sheet?.get("pa:slate"),
      sceneRef?.path,
    );
    await teamRef.update(
      cardNumberAttrs.reduce(
        (data, k) => ({ ...data, [k]: sheet?.get(k) || FieldValue.delete() }),
        {
          latestSlate: nextCandidate || FieldValue.delete(),
        },
      ),
    );
    return nextCandidate;
  }
  return latest;
}

export async function updateLatestSlate(
  teamRef: DocumentReference | undefined,
  slate: string | undefined,
  oldSlate: string | undefined,
  sheetId: string | undefined,
  sceneRef: DocumentReference | undefined,
) {
  if (!teamRef) return;

  const latest = await removeSlate(teamRef, sheetId, oldSlate, sceneRef);
  const candidate = parseSlateNumber(slate, sceneRef?.path);
  if (
    !candidate ||
    (latest !== undefined &&
      latest.prefix === candidate.prefix &&
      latest.n >= candidate.n)
  ) {
    return;
  }
  await teamRef.update("latestSlate", candidate);
  // TODO Handle exceptions
}

export async function generateNextSlateAttrs(
  sceneRef: DocumentReference | undefined,
  noSlates: boolean,
): Promise<DocumentData> {
  let attrs = {} as DocumentData;
  if (!sceneRef) return attrs;
  const teamRef = (await sceneRef.get()).get("team");
  if (!teamRef) return attrs;
  attrs["team"] = teamRef;
  const team = await teamRef.get();
  const latest: SlateNumber | undefined = team.get("latestSlate");
  const slate: SlateNumber | undefined =
    latest && !(noSlates && latest.scenePath !== sceneRef.path)
      ? {
          ...latest,
          n: latest.n + 1,
        }
      : noSlates
        ? { scenePath: sceneRef.path, prefix: "", n: 1 } // TODO Should take existing sheets for scene into account (using the query in removeSlate above), but will that cause problems for working offline?
        : undefined;
  if (slate) {
    teamRef.update("latestSlate", slate);
    attrs["pa:slate"] = formatSlateNumber(slate);
  }
  for (const k of cardNumberAttrs) {
    const v = team.get(k);
    if (v) attrs[k] = v;
  }
  const allCameras: string[] | undefined = team.get("cameras");
  const prevCameras: string[] | undefined = team?.get("prevCamerasSel");
  const cameras =
    prevCameras?.filter((camera) => allCameras?.includes(camera)) ?? allCameras;
  if (cameras) attrs["pa:cameras"] = cameras;
  return attrs;
}

export function useTeams() {
  return useDocuments(useProject()?.collection("teams"));
}
